import { Domain as AuthDomain } from 'auth-common/domain';
import type { Tagged } from 'type-fest';
import type { AggregationRoot } from '../common';
import { SystemImmutableEntity as SIE } from '../common';
import { UniqueId } from '../id';
import type { MetadataField } from '../metadataField';
import { Session, SessionIdentifier } from '../session';
import { User, UserIdentifier } from '../user';

import Project = AuthDomain.Object.Project;

export type Event = AggregationRoot &
  SIE & {
    id: Event.Id;
    projectId: Project.Id;
    name: string;
    sessionId: Session.Id;
    userId: User.Id;
    sessionIdentifiers: SessionIdentifier.Identifier[];
    userIdentifiers: UserIdentifier.Identifier[];
    temporaryUserIdentifiers: UserIdentifier.Identifier[];
    issuer: string;
    metadata?: Event.Metadata;
  };

export namespace Event {
  export type Id = Tagged<UniqueId, 'eventId'>;
  export namespace Id {
    export const Generate = () => UniqueId.generate() as Id;
    export function FromString(id: string): Id {
      return UniqueId.FromString(id) as Id;
    }
  }

  export type Metadata = Record<string, string | number | boolean>;

  export namespace Construct {
    export type Props = {
      id: string;
      projectId: string;
      name: string;
      sessionId: string;
      userId: string;
      sessionIdentifiers: { type: string; value: string }[];
      userIdentifiers: { type: string; value: string }[];
      temporaryUserIdentifiers?: { type: string; value: string }[];
      issuer: string;
      metadata?: Metadata;
    } & SIE.Props;
  }

  export const Construct = (props: Construct.Props): Event => {
    return {
      id: Id.FromString(props.id),
      projectId: Project.Id.FromString(props.projectId),
      name: props.name,
      sessionId: Session.Id.FromString(props.sessionId),
      userId: User.Id.FromString(props.userId),
      sessionIdentifiers: props.sessionIdentifiers.map(SessionIdentifier.Identifier.Construct),
      userIdentifiers: props.userIdentifiers.map(UserIdentifier.Identifier.Construct),
      temporaryUserIdentifiers: props.temporaryUserIdentifiers?.map(UserIdentifier.Identifier.Construct) ?? [],
      issuer: props.issuer,
      metadata: props.metadata,
      ...SIE.Construct(props),
    };
  };

  export namespace Create {
    export type Props = Omit<Construct.Props, 'id' | 'createdAt'>;
  }
  export const Create = (props: Create.Props): Event => {
    return Construct({ id: Id.Generate(), ...props, ...SIE.Create() });
  };

  export const IsEventFor = (event: Event, sessionId: Session.Id): boolean => {
    return event.sessionId === sessionId || event.sessionIdentifiers.some((id) => id.type === 'sessionId' && id.value === sessionId);
  };

  const castValue = (value: string | number | boolean, dataType: MetadataField.DataType): string | number | boolean => {
    switch (dataType) {
      case 'string':
        return String(value);
      case 'number': {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      }
      case 'boolean':
        return value === true || value === 'true' || value === 1 || value === '1';
    }
  };

  export const CastMetadata = (metadata: Metadata | undefined, metadataFields: MetadataField[]): Metadata | undefined => {
    if (!metadata) return undefined;
    const fieldMap = new Map(metadataFields.map((f) => [f.key as string, f.dataType]));
    return Object.fromEntries(
      Object.entries(metadata).map(([key, value]) => {
        const dataType = fieldMap.get(key);
        if (!dataType) return [key, value];
        return [key, castValue(value, dataType)];
      }),
    );
  };
}
